import React, { useState } from 'react'
import { useQuery } from 'react-query'

import makeStyles from '@mui/styles/makeStyles'
import { Button } from '@mui/material'

import { useRouter } from '@shared/hooks/use-router'
import { apiJSON } from '@shared/utils/api/api.util'

import { Roles, SharedFile } from '@shared/types'
import { ConfirmationDialog } from '@shared/components/confirmation.dialog.component'
import { useResourceRemove } from '@shared/hooks/use-resource-remove.hook'
import { Table } from '@shared/components/table/table.component'
import { originalIcons } from '@shared/components/icons/icons'
import { isArray } from 'lodash'
import { MaterialTableProps } from '@material-table/core'
import { useACL } from '@shared/hooks/use-acl.hook'
import handleDownload from '@shared/utils/handle-download'
import { FilesDialog } from './files.dialog.component'

export const filesEndpoint = `sharedFiles`

export function FilesPage() {
  const { is } = useACL()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const isModifyAllowed = is([Roles.Admin])
  const { navigate, location } = useRouter()

  const {
    isRemovalDialogOpened,
    isResourceRemoving,
    setRemovalDialogOpened,
    setRemovedResourceID,
    onResourceRemove,
    isInvalidatingAfterRemove,
  } = useResourceRemove(filesEndpoint)

  const {
    data: sharedFiles,
    isLoading: areFilesLoading,
    refetch,
  } = useQuery<SharedFile[]>(filesEndpoint, () => apiJSON(filesEndpoint))

  const handleReloadAfterCreate = () => {
    refetch()
    setIsAddDialogOpen(false)
  }

  return (
    <>
      {isAddDialogOpen ? (
        <FilesDialog
          onClose={() => setIsAddDialogOpen(false)}
          afterCreate={handleReloadAfterCreate}
        />
      ) : null}
      <Table<SharedFile>
        isLoading={areFilesLoading || isInvalidatingAfterRemove}
        title="Dokumenty udostępnione"
        columns={[{ title: 'Nazwa', field: 'name' }]}
        data={sharedFiles ?? []}
        options={{
          pageSize: 20,
        }}
        actions={
          isModifyAllowed
            ? ([
                {
                  icon: originalIcons.Download,
                  tooltip: 'Pobierz',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      handleDownload(
                        `${filesEndpoint}/${resourceEntity.id}/file`,
                        resourceEntity?.fileName
                      )
                    }
                  },
                },
                {
                  icon: originalIcons.Visibility,
                  tooltip: 'Podgląd',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      window.open(resourceEntity.fileUrl, '_blank')
                    }
                  },
                },
                {
                  icon: originalIcons.Create,
                  tooltip: 'Edytuj',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      navigate(`/files/edit/${resourceEntity.id}`, {
                        state: {
                          backgroundLocation: location,
                        },
                      })
                    }
                  },
                },
                {
                  icon: originalIcons.Delete,
                  tooltip: 'Usuń',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      setRemovedResourceID(resourceEntity.id)
                      setRemovalDialogOpened(true)
                    }
                  },
                },
                {
                  icon: originalIcons.Add,
                  isFreeAction: true,
                  onClick: () => setIsAddDialogOpen(true),
                },
              ] as MaterialTableProps<any>['actions'])
            : ([
                {
                  icon: originalIcons.Download,
                  tooltip: 'Pobierz',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity: SharedFile
                  ) => {
                    if (!isArray(resourceEntity)) {
                      handleDownload(
                        `${filesEndpoint}/${resourceEntity.id}/file`,
                        resourceEntity?.fileName
                      )
                    }
                  },
                },
                {
                  icon: originalIcons.Visibility,
                  tooltip: 'Podgląd',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      window.open(resourceEntity.fileUrl, '_blank')
                    }
                  },
                },
              ] as MaterialTableProps<any>['actions'])
        }
      />
      <ConfirmationDialog
        submitButtonProps={{
          children: 'Usuń',
          isLoading: isResourceRemoving,
        }}
        title="Potwierdzenie usunięcia"
        onClose={onResourceRemove}
        open={isRemovalDialogOpened}
      >
        Czy napewno chcesz usunąć dany wiersz?
      </ConfirmationDialog>
      <Button
        variant="contained"
        onClick={() => navigate(-1)}
        style={{ backgroundColor: '#C0C0C0' }}
      >
        Powrót
      </Button>
    </>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    actionText: {
      textDecoration: 'underline',
      textAlign: 'right',
      cursor: 'pointer',
      display: 'inline-block',
      marginRight: theme.spacing(2),
    },
  }
})
